import React, { useState } from 'react';
import { saveOnboardingProfile, deriveTierFromRoleAndExam } from '../firebase/userProfile.js';
import { getAuthInstance, trackEvent, setAnalyticsUserProps } from '../firebase/config.js';
import { resolveGenerationExamType } from './clinical/clinicalOptions.js';
import { FREE_TRIAL_TOTAL } from './FreeTrialBanner.jsx';

const ONBOARDING_KEY         = 'ff:onboarding:completed';
const ONBOARDING_PROFILE_KEY = 'ff:onboarding:profile';

export function markOnboardingNeeded() {
    try {
        localStorage.setItem(ONBOARDING_KEY, 'pending');
        window.dispatchEvent(new CustomEvent('ff:onboarding:needed'));
    } catch (_) {}
}

export function isOnboardingNeeded() {
    try { return localStorage.getItem(ONBOARDING_KEY) === 'pending'; } catch (_) { return false; }
}

export function completeOnboarding(profile) {
    try {
        localStorage.setItem(ONBOARDING_KEY, 'done');
        if (profile) localStorage.setItem(ONBOARDING_PROFILE_KEY, JSON.stringify(profile));
    } catch (_) {}
}

export function getOnboardingProfile() {
    try { return JSON.parse(localStorage.getItem(ONBOARDING_PROFILE_KEY) || 'null'); } catch (_) { return null; }
}

// ── Role options ──────────────────────────────────────────────────────────────

export const ROLES = [
    {
        id: 'ms1_ms2',
        label: 'Medical Student — MS1/MS2',
        sub: 'Preclinical block exams',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
        ),
        tier: 'med_student',
        color: 'teal',
    },
    {
        id: 'ms3_ms4',
        label: 'Medical Student — MS3/MS4',
        sub: 'Shelf exams & Step prep',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
        ),
        tier: 'med_student',
        color: 'teal',
    },
    {
        id: 'resident_fellow',
        label: 'Resident / Fellow',
        sub: 'Step 3, COMLEX 3, specialty boards',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
        tier: 'board_review',
        color: 'amber',
    },
    {
        id: 'nursing_student',
        label: 'Nursing Student — RN / PN',
        sub: 'Nursing school block exams',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
        tier: 'med_student',
        color: 'teal',
    },
    {
        id: 'nclex_prep',
        label: 'NCLEX-RN / NCLEX-PN Prep',
        sub: 'Licensure exam, ready to sit',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
        tier: 'board_review',
        color: 'amber',
    },
    {
        id: 'np_pa_aa_student',
        label: 'NP / PA / AA — Student',
        sub: 'Didactic phase, block exams',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
        ),
        tier: 'med_student',
        color: 'teal',
    },
    {
        id: 'np_pa_crna_caa',
        label: 'NP / PA / CRNA / CAA',
        sub: 'Licensure & board certification',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
        tier: 'board_review',
        color: 'amber',
    },
    {
        id: 'attending_initial',
        label: 'Attending — Initial Certification',
        sub: 'Specialty board exam prep',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
        ),
        tier: 'specialty_boards',
        color: 'sky',
    },
    {
        id: 'attending_moc',
        label: 'Attending — MOC / Recertification',
        sub: 'Maintenance of certification',
        icon: (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
        ),
        tier: 'specialty_boards',
        color: 'sky',
    },
];

export const ROLE_COLORS = {
    teal:  { border: 'border-teal-400/50 bg-teal-500/10 ring-1 ring-teal-400/25',   text: 'text-teal-300',  dot: 'bg-teal-500' },
    amber: { border: 'border-amber-400/50 bg-amber-500/10 ring-1 ring-amber-400/25', text: 'text-amber-300', dot: 'bg-amber-500' },
    sky:   { border: 'border-sky-400/50 bg-sky-500/10 ring-1 ring-sky-400/25',       text: 'text-sky-300',   dot: 'bg-sky-500' },
};

// ── Step 2 options — contextual on role ──────────────────────────────────────

const CLASS_PREP_SUBJECTS = [
    { id: 'anatomy',      label: 'Anatomy',      sub: 'Gross, histology, embryology' },
    { id: 'biochemistry', label: 'Biochemistry',  sub: 'Pathways, enzymes, storage diseases' },
    { id: 'physiology',   label: 'Physiology',    sub: 'Mechanisms, ions, fluid balance' },
    { id: 'pharmacology', label: 'Pharmacology',  sub: 'MOA, receptors, adverse effects' },
    { id: 'microbiology', label: 'Microbiology',  sub: 'Organisms, virulence, antibiotics' },
    { id: 'pathology',    label: 'Pathology',     sub: 'Gross, microscopic, disease process' },
    { id: 'immunology',   label: 'Immunology',    sub: 'Cell types, hypersensitivity, complement' },
    { id: 'neuroscience', label: 'Neuroscience',  sub: 'Tracts, localization, neurotransmitters' },
    { id: 'step1',   label: 'USMLE Step 1 Review (Integrated)',   sub: 'Board-style vignettes across all basic sciences' },
    { id: 'comlex1', label: 'COMLEX Level 1 Review (Integrated)', sub: 'Board-style + OMM basic science review' },
];

const NURSING_SUBJECTS = [
    { id: 'nursing_fundamentals', label: 'Fundamentals of Nursing',         sub: 'Nursing process, safety, vitals, communication' },
    { id: 'nursing_medsurg',      label: 'Medical-Surgical Nursing',         sub: 'Adult health across all body systems' },
    { id: 'nursing_maternal',     label: 'Maternal-Newborn Nursing',         sub: 'OB, labor & delivery, postpartum, newborn' },
    { id: 'nursing_pediatric',    label: 'Pediatric Nursing',               sub: 'Growth, development, childhood illness' },
    { id: 'nursing_psych',        label: 'Psychiatric-Mental Health',        sub: 'Therapeutic comm., safety, psychopharm' },
    { id: 'nursing_community',    label: 'Community & Public Health',        sub: 'Epidemiology, prevention, populations' },
    { id: 'nursing_leadership',   label: 'Leadership & Delegation',          sub: 'Delegation, prioritization, SBAR, ethics' },
    { id: 'nursing_pharmacology', label: 'Pharmacology for Nursing',         sub: 'Drug mechanisms, adverse effects, patient teaching' },
    { id: 'nursing_general',      label: 'General Nursing (All Topics)',    sub: 'For LPN/RN programs without separate block courses' },
];

const NCLEX_OPTIONS = [
    { id: 'nclexRn', label: 'NCLEX-RN', sub: 'Registered Nurse licensure — independent judgment & delegation' },
    { id: 'nclexPn', label: 'NCLEX-PN', sub: 'Practical/Vocational Nurse licensure — directed care under an RN' },
];

const MS3_FOCUS_OPTIONS = [
    { id: 'shelf',   label: 'Shelf Exams',           sub: 'Internal medicine, surgery, pediatrics, ob/gyn, psych, FM, neuro' },
    { id: 'step2',   label: 'USMLE Step 2 CK',       sub: 'Clinical knowledge & management' },
    { id: 'step1',   label: 'USMLE Step 1 Review',   sub: 'Basic science reinforcement' },
    { id: 'comlex2', label: 'COMLEX Level 2',        sub: 'Clinical + OMM integration' },
    { id: 'comlex1', label: 'COMLEX Level 1 Review', sub: 'Basic science reinforcement (osteopathic)' },
];

const RESIDENT_FOCUS_OPTIONS = [
    { id: 'step3',            label: 'USMLE Step 3',          sub: 'Clinical management & guidelines' },
    { id: 'comlex3',          label: 'COMLEX Level 3',        sub: 'Clinical management + OMM' },
    { id: 'internalMedicine', label: 'Internal Medicine (ABIM)', sub: 'Specialty board certification' },
    { id: 'surgery',          label: 'General Surgery (ABS)', sub: 'Specialty board certification' },
    { id: 'emergencyMedicine',label: 'Emergency Medicine (ABEM)', sub: 'Specialty board certification' },
    { id: 'obgyn',            label: 'OB/GYN (ABOG)',         sub: 'Specialty board certification' },
    { id: 'psychiatry',       label: 'Psychiatry (ABPN)',     sub: 'Specialty board certification' },
    { id: 'familyMedicine',   label: 'Family Medicine (ABFM)',sub: 'Specialty board certification' },
    { id: 'pediatrics',       label: 'Pediatrics (ABP)',      sub: 'Specialty board certification' },
    { id: 'specialtyBoard',   label: 'Other Residency / Fellowship', sub: 'General specialty board content' },
];

const NP_PA_OPTIONS = [
    { id: 'fnp',   label: 'Family Nurse Practitioner (FNP)', sub: 'AANP or ANCC certification' },
    { id: 'acnp',  label: 'Acute Care NP (ACNP)',            sub: 'AANP or ANCC certification' },
    { id: 'pance', label: 'PA-C (PANCE/PANRE)',              sub: 'Physician assistant certification' },
    { id: 'crna',  label: 'CRNA (NBCRNA)',                   sub: 'Certified registered nurse anesthetist' },
    { id: 'caa',   label: 'CAA (ABA)',                       sub: 'Certified anesthesiologist assistant' },
    { id: 'npPaGeneral', label: 'Other NP/PA/CRNA Program',  sub: 'General advanced-practice certification prep' },
];

const ATTENDING_BOARD_OPTIONS = [
    { id: 'internalMedicine', label: 'Internal Medicine (ABIM)' },
    { id: 'familyMedicine',   label: 'Family Medicine (ABFM)' },
    { id: 'surgery',          label: 'General Surgery (ABS)' },
    { id: 'emergencyMedicine',label: 'Emergency Medicine (ABEM)' },
    { id: 'pediatrics',       label: 'Pediatrics (ABP)' },
    { id: 'obgyn',            label: 'OB/GYN (ABOG)' },
    { id: 'psychiatry',       label: 'Psychiatry (ABPN)' },
    { id: 'dermatology',      label: 'Dermatology (ABD)' },
    { id: 'ophthalmology',    label: 'Ophthalmology (ABO)' },
    { id: 'ent',              label: 'Otolaryngology (ABOHNS)' },
    { id: 'specialtyBoard',   label: 'Other Specialty Board' },
];

export function getStep2Config(role) {
    switch (role) {
        // Was a multi-select "which subjects are you studying" step, requiring users to
        // pre-declare a subset before they could generate anything. Every subject in the
        // list is always available for generation regardless — this only ever picked the
        // starting default — so it's simplified to the same single-pick pattern every
        // other role already uses. Switch to any subject anytime in Settings.
        case 'ms1_ms2':
        case 'np_pa_aa_student': return { type: 'single',  label: 'Which subject do you want to start with?', sub: 'You can generate for any subject anytime — pick where to start.', options: CLASS_PREP_SUBJECTS, multi: false };
        case 'nursing_student':  return { type: 'single',  label: 'Which course do you want to start with?',  sub: 'You can generate for any course anytime — pick where to start.',  options: NURSING_SUBJECTS,   multi: false };
        case 'nclex_prep':       return { type: 'single',    label: 'Which exam are you preparing for?',  sub: 'We\'ll match questions to RN or PN scope and delegation rules.',            options: NCLEX_OPTIONS,       multi: false };
        case 'ms3_ms4':          return { type: 'single',    label: 'What is your primary focus?',        sub: 'We\'ll tune the question format to match your exam.',                       options: MS3_FOCUS_OPTIONS,   multi: false };
        case 'resident_fellow':  return { type: 'single',    label: 'What are you preparing for?',        sub: 'This sets your question tier and format.',                                  options: RESIDENT_FOCUS_OPTIONS, multi: false };
        case 'np_pa_crna_caa':   return { type: 'single',    label: 'Which program are you in?',          sub: 'We\'ll match questions to your certification exam.',                         options: NP_PA_OPTIONS,       multi: false };
        case 'attending_initial':
        case 'attending_moc':    return { type: 'single',    label: 'Which specialty board?',             sub: 'We\'ll tailor questions to your board\'s content outline.',                  options: ATTENDING_BOARD_OPTIONS, multi: false };
        default:                 return null;
    }
}

// ── Tour slides ───────────────────────────────────────────────────────────────

const TOUR_SLIDES = [
    {
        badge: 'Feature 1 of 3',
        color: 'teal',
        icon: (
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 ring-1 ring-teal-400/20">
                <svg className="h-8 w-8 text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
            </div>
        ),
        title: 'Capture Your Lectures',
        desc: 'Record lectures live or upload any audio/video file. FlashFonic transcribes the content and generates study-ready flashcards automatically.',
        bullets: ['Record live in class', 'Upload audio, video, or podcast', 'Cards generated in seconds'],
    },
    {
        badge: 'Feature 2 of 3',
        color: 'amber',
        icon: (
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 ring-1 ring-amber-400/20">
                <svg className="h-8 w-8 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
            </div>
        ),
        title: 'Turn Notes Into Exam Questions',
        desc: 'Paste your lecture notes or upload a photo of your slides. FlashFonic generates block-exam style questions tuned to your subject and level.',
        bullets: ['Questions styled for your specific exam', 'Block exam, shelf, Step, or boards', 'Accuracy-checked before you see them'],
    },
    {
        badge: 'Feature 3 of 3',
        color: 'emerald',
        icon: (
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 ring-1 ring-emerald-400/20">
                <svg className="h-8 w-8 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            </div>
        ),
        title: 'Track Your Progress',
        desc: 'Your questions build into a personal Q-bank. Review performance by subject, see where you\'re weakest, and study smarter with every session.',
        bullets: ['Personal Q-bank that grows with you', 'Subject-level performance tracking', 'Reusable questions from your own material'],
    },
];

const SLIDE_COLORS = {
    teal:    { bullet: 'bg-teal-400',    badge: 'text-teal-300 bg-teal-500/10 border-teal-400/20',       btn: 'from-teal-500 to-emerald-500' },
    amber:   { bullet: 'bg-amber-400',   badge: 'text-amber-300 bg-amber-500/10 border-amber-400/20',     btn: 'from-amber-500 to-orange-500' },
    emerald: { bullet: 'bg-emerald-400', badge: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/20', btn: 'from-emerald-500 to-teal-500' },
};

const TOTAL_STEPS = 2 + TOUR_SLIDES.length; // role + focus + 3 tour slides

// ── Component ─────────────────────────────────────────────────────────────────

const OnboardingWizard = ({ onComplete }) => {
    const [step, setStep]           = useState(0);
    const [role, setRole]           = useState('');
    const [examTarget, setExamTarget] = useState('');
    const [subjects, setSubjects]   = useState([]);

    const step2Config = role ? getStep2Config(role) : null;
    const slideIndex  = step - 2;
    const isSlide     = step >= 2 && step < 2 + TOUR_SLIDES.length;
    const isDone      = step >= 2 + TOUR_SLIDES.length;
    const progress    = Math.round((step / TOTAL_STEPS) * 100);

    const toggleSubject = (id) => {
        setSubjects(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const step1Valid = !!role;
    const step2Valid = step2Config?.multi
        ? subjects.length > 0
        : !!examTarget;

    const canContinue = step === 0 ? step1Valid : step === 1 ? step2Valid : true;

    const buildProfile = () => {
        const defaultExamTarget = step2Config?.multi
            ? (subjects[0] || 'anatomy')
            : examTarget;
        const tier = deriveTierFromRoleAndExam(role, defaultExamTarget);
        return {
            role,
            examTarget: defaultExamTarget,
            subjects: step2Config?.multi ? subjects : [],
            tier,
            focus: step2Config?.multi ? 'class_prep' : defaultExamTarget,
        };
    };

    const handleFinish = async () => {
        const profile = buildProfile();
        completeOnboarding(profile);
        // Persist to Firestore for authenticated users
        try {
            const auth = getAuthInstance?.();
            const uid  = auth?.currentUser?.uid;
            if (uid && !auth.currentUser.isAnonymous) {
                await saveOnboardingProfile(uid, profile);
            }
        } catch (_) {}
        try {
            const examType = resolveGenerationExamType({
                examTarget: profile.examTarget,
                subjects: profile.subjects,
                isMultiSubject: step2Config?.multi,
            });
            setAnalyticsUserProps({ prep_tier: profile.tier, exam_type: examType });
            trackEvent('prep_level_set', { role: profile.role, examType, tier: profile.tier, entry_point: 'onboarding' });
        } catch (_) {}
        onComplete?.(profile);
    };

    const next = () => {
        if (isDone) { handleFinish(); return; }
        setStep(s => s + 1);
    };
    const back = () => {
        setStep(s => Math.max(0, s - 1));
        if (step === 1) { setExamTarget(''); setSubjects([]); }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div
                className="relative w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0d1628] shadow-2xl overflow-hidden"
                style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif' }}
            >
                {/* Progress bar */}
                <div className="h-1 bg-white/5">
                    <div
                        className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <div className="p-6 sm:p-8">

                    {/* ── Step 0: Role ─────────────────────────────────────── */}
                    {step === 0 && (
                        <div>
                            <div className="mb-5">
                                <span className="inline-block text-xs text-teal-300 font-medium mb-2">Step 1 of 2</span>
                                <h2 className="text-xl font-semibold text-white tracking-tight">Welcome to FlashFonic</h2>
                                <p className="text-sm text-slate-400 mt-1">Tell us where you are in your training so we can personalize your questions.</p>
                            </div>
                            <div className="space-y-2">
                                {ROLES.map(({ id, label, sub, icon, color }) => {
                                    const colors = ROLE_COLORS[color];
                                    return (
                                        <button
                                            key={id}
                                            onClick={() => { setRole(id); setExamTarget(''); setSubjects([]); }}
                                            className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                                                role === id ? colors.border : 'border-white/10 bg-slate-900/40 hover:border-white/20 hover:bg-slate-900/60'
                                            }`}
                                        >
                                            <span className={`shrink-0 ${role === id ? colors.text : 'text-slate-500'}`}>
                                                {icon}
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <span className={`block text-sm font-medium ${role === id ? 'text-white' : 'text-slate-200'}`}>{label}</span>
                                                <span className="block text-[11px] text-slate-500 mt-0.5">{sub}</span>
                                            </span>
                                            {role === id && (
                                                <span className={`shrink-0 flex h-4 w-4 items-center justify-center rounded-full ${colors.dot}`}>
                                                    <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Step 1: Contextual focus/subject selection ─────── */}
                    {step === 1 && step2Config && (
                        <div>
                            <div className="mb-5">
                                <span className="inline-block text-xs text-amber-300 font-medium mb-2">Step 2 of 2</span>
                                <h2 className="text-xl font-semibold text-white tracking-tight">{step2Config.label}</h2>
                                <p className="text-sm text-slate-400 mt-1">{step2Config.sub}</p>
                            </div>

                            {step2Config.multi ? (
                                /* Multi-select grid for class prep subjects */
                                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                                    {step2Config.options.map(({ id, label, sub }) => {
                                        const selected = subjects.includes(id);
                                        return (
                                            <button
                                                key={id}
                                                onClick={() => toggleSubject(id)}
                                                className={`flex flex-col items-start rounded-xl border p-3 text-left transition ${
                                                    selected
                                                        ? 'border-teal-400/50 bg-teal-500/10 ring-1 ring-teal-400/25'
                                                        : 'border-white/10 bg-slate-900/40 hover:border-white/20'
                                                }`}
                                            >
                                                <span className={`text-sm font-medium ${selected ? 'text-teal-200' : 'text-white'}`}>{label}</span>
                                                <span className="text-[11px] text-slate-500 mt-0.5 leading-snug">{sub}</span>
                                                {selected && (
                                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-teal-400" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                /* Single-select list */
                                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                    {step2Config.options.map(({ id, label, sub }) => (
                                        <button
                                            key={id}
                                            onClick={() => setExamTarget(id)}
                                            className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${
                                                examTarget === id
                                                    ? 'border-amber-400/50 bg-amber-500/10 ring-1 ring-amber-400/25'
                                                    : 'border-white/10 bg-slate-900/40 hover:border-white/20 hover:bg-slate-900/60'
                                            }`}
                                        >
                                            <span className="flex-1 min-w-0">
                                                <span className={`block text-sm font-medium ${examTarget === id ? 'text-amber-200' : 'text-white'}`}>{label}</span>
                                                {sub && <span className="block text-[11px] text-slate-500 mt-0.5">{sub}</span>}
                                            </span>
                                            {examTarget === id && (
                                                <span className="shrink-0 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500">
                                                    <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Tour slides ──────────────────────────────────────── */}
                    {isSlide && (() => {
                        const slide  = TOUR_SLIDES[slideIndex];
                        const colors = SLIDE_COLORS[slide.color];
                        return (
                            <div className="text-center">
                                <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full border mb-5 ${colors.badge}`}>
                                    {slide.badge}
                                </span>
                                <div className="flex justify-center mb-5">{slide.icon}</div>
                                <h2 className="text-xl font-semibold text-white tracking-tight">{slide.title}</h2>
                                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{slide.desc}</p>
                                <ul className="mt-5 space-y-2 text-left">
                                    {slide.bullets.map(b => (
                                        <li key={b} className="flex items-center gap-2.5 text-sm text-slate-300">
                                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${colors.bullet}`} />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })()}

                    {/* ── Done ─────────────────────────────────────────────── */}
                    {isDone && (
                        <div className="text-center py-4">
                            <div className="flex justify-center mb-4">
                                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500/20 to-emerald-500/20 ring-1 ring-teal-400/20">
                                    <svg className="h-8 w-8 text-teal-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                    </svg>
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold text-white tracking-tight">You're all set!</h2>
                            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                                Your profile is saved. Start by pasting your lecture notes to generate your first exam questions.
                            </p>
                            <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                                <span className="flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                                    {FREE_TRIAL_TOTAL} free questions included
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                    No credit card required
                                </span>
                            </div>
                        </div>
                    )}

                    {/* ── Navigation ───────────────────────────────────────── */}
                    <div className={`mt-6 flex items-center ${step > 0 ? 'justify-between' : 'justify-end'}`}>
                        {step > 0 && (
                            <button
                                onClick={back}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-slate-400 hover:text-white transition"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back
                            </button>
                        )}
                        <button
                            onClick={next}
                            disabled={!canContinue}
                            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-emerald-500 shadow-lg shadow-teal-500/20 hover:opacity-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {isDone ? 'Start Learning' : 'Continue'}
                            {!isDone && (
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {!isDone && (
                        <div className="mt-3 text-center">
                            <button onClick={handleFinish} className="text-xs text-slate-600 hover:text-slate-400 transition">
                                Skip setup
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingWizard;
